import React, { useState, useEffect } from 'react';
import { Course, TeamMember } from '../../types';
import { courseApi, teamApi } from '../../services/api';
import Modal from '../../components/Modal';
import { PlusCircleIcon, PencilIcon, TrashIcon, BookOpenIcon, DocumentTextIcon, ViewGridIcon, ViewListIcon } from '../../components/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import AdminButton from '../../components/admin/AdminButton';

type CourseFormState = Omit<Course, 'instructor'> & { instructor: string };

const ManageCoursesPage: React.FC = () => {
  const { colors } = useTheme();
  const [courses, setCourses] = useState<Course[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCourse, setCurrentCourse] = useState<Partial<CourseFormState> | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const navigate = useNavigate();

  useEffect(() => {
    fetchCourses();
    fetchTeamMembers();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await courseApi.getAllCourses();
      setCourses(response.data.data.courses);
      setError(null);
    } catch (err) {
      setError('Failed to load courses.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await teamApi.getAll();
      setTeamMembers(response.data.data.members);
    } catch (err) {
      // Optionally handle error
    }
  };

  const openModalForNew = () => {
    setIsEditing(false);
    setCurrentCourse({ title: '', tagline: '', description: '', longDescription: '', curriculum: [], price: 0, duration: '0', instructor: '', imageUrl: '', category: '' });
    setIsModalOpen(true);
  };

  const openModalForEdit = (course: Course) => {
    setIsEditing(true);
    let instructorId = '';
    if (typeof course.instructor === 'string') {
      instructorId = course.instructor;
    } else if (course.instructor && typeof course.instructor === 'object') {
      instructorId = course.instructor._id;
    }
    const { instructor, ...rest } = course;
    setCurrentCourse({ ...rest, instructor: instructorId, duration: course.duration.toString() });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentCourse(null);
  };

  const handleDelete = async (courseId: string) => {
    if (!window.confirm('Are you sure you want to delete this course? This will also delete all course details.')) return;
    try {
      // Delete course details first
      await courseApi.deleteCourseDetail(courseId);
      // Then delete the course
      await courseApi.deleteCourse(courseId);
      await fetchCourses();
    } catch (err) {
      alert('Failed to delete course.');
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (!currentCourse) return;
    const { name, value } = e.target;
    if (name === 'curriculum') {
      setCurrentCourse({ ...currentCourse, [name]: value.split(',').map(item => item.trim()) });
    } else if (name === 'price' || name === 'duration') {
      setCurrentCourse({ ...currentCourse, [name]: parseFloat(value) });
    } else {
      setCurrentCourse({ ...currentCourse, [name]: value });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentCourse) return;
    setSaving(true);
    try {
      const payload: any = { ...currentCourse };
      if (payload.instructor) {
        payload.instructor = { _id: payload.instructor };
      }
      if (isEditing && currentCourse._id) {
        await courseApi.updateCourse(currentCourse._id, payload);
      } else {
        await courseApi.createCourse(payload);
      }
      await fetchCourses();
      closeModal();
    } catch (err) {
      alert('Failed to save course.');
    } finally {
      setSaving(false);
    }
  };

  const courseCategories = ['Web Development', 'Data Science', 'Design', 'Cloud Computing', 'Other'];

  const handleAddCourse = () => {
    navigate('/admin/courses/new');
  };

  const handleEditCourse = (courseId: string) => {
    navigate(`/admin/courses/edit/${courseId}`);
  };

  const handleViewCourse = (courseId: string) => {
    navigate(`/courses/${courseId}`);
  };

  const handleManageCourseDetails = (courseId: string) => {
    navigate(`/admin/courses/${courseId}/details`);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold" style={{ color: colors.text.primary }}>Manage Courses</h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center rounded-lg p-1" style={{ backgroundColor: colors.background.paper }}>
            <button
              onClick={() => setViewMode('grid')}
              className="p-2 rounded-lg transition-all duration-300"
              style={{
                background: viewMode === 'grid' ? `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)` : 'transparent',
                color: viewMode === 'grid' ? 'white' : colors.text.secondary
              }}
            >
              <ViewGridIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className="p-2 rounded-lg transition-all duration-300"
              style={{
                background: viewMode === 'table' ? `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)` : 'transparent',
                color: viewMode === 'table' ? 'white' : colors.text.secondary
              }}
            >
              <ViewListIcon className="w-5 h-5" />
            </button>
          </div>
          <AdminButton onClick={handleAddCourse} size="md">
            <div className="flex items-center">
              <PlusCircleIcon className="w-5 h-5 mr-2" />
              Add New Course
            </div>
          </AdminButton>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center items-center min-h-[100px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <p className="text-red-500 mb-4">{error}</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => (
            <div
              key={course._id}
              className="bg-neutral-dark rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
              onClick={() => handleManageCourseDetails(course._id)}
            >
              <div className="aspect-video bg-neutral-darker">
                <img
                  src={course.imageUrl || 'https://via.placeholder.com/400x225?text=No+Image'}
                  alt={course.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-white mb-2">{course.title}</h3>
                <p className="text-neutral-400 text-sm mb-4">{course.tagline}</p>
                <div className="flex items-center justify-between">
                  <span className="text-primary font-medium">${course.price.toFixed(2)}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openModalForEdit(course);
                      }}
                      className="text-primary-light hover:text-primary p-2 rounded-lg hover:bg-primary/10 transition-colors"
                      title="Edit Course"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(course._id);
                      }}
                      className="text-red-500 hover:text-red-400 p-2 rounded-lg hover:bg-red-500/10 transition-colors"
                      title="Delete Course"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-neutral-dark shadow-xl rounded-lg overflow-x-auto">
          <table className="w-full text-sm text-left text-neutral-300">
            <thead className="text-xs text-neutral-100 uppercase bg-neutral-darker">
              <tr>
                <th scope="col" className="px-6 py-3">Title</th>
                <th scope="col" className="px-6 py-3">Category</th>
                <th scope="col" className="px-6 py-3">Price</th>
                <th scope="col" className="px-6 py-3">Instructor</th>
                <th scope="col" className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((course) => (
                <tr key={course._id} className="bg-neutral-dark border-b border-neutral-700 hover:bg-neutral-dark/70">
                  <td className="px-6 py-4 font-medium text-white whitespace-nowrap">{course.title}</td>
                  <td className="px-6 py-4">{course.category}</td>
                  <td className="px-6 py-4">${course.price.toFixed(2)}</td>
                  <td className="px-6 py-4">{
                    teamMembers.find(m => m._id === (course.instructor && typeof course.instructor === 'object' ? course.instructor._id : course.instructor))?.name || ''
                  }</td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <Link
                      to={`/courses/${course._id}`}
                      className="text-primary-light hover:text-primary p-2 rounded-lg hover:bg-primary/10 transition-colors inline-flex items-center"
                      title="View Course"
                    >
                      <BookOpenIcon className="w-5 h-5" />
                    </Link>
                    <button
                      onClick={() => openModalForEdit(course)}
                      className="text-primary-light hover:text-primary p-2 rounded-lg hover:bg-primary/10 transition-colors"
                      title="Edit Course"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleManageCourseDetails(course._id)}
                      className="text-primary-light hover:text-primary p-2 rounded-lg hover:bg-primary/10 transition-colors"
                      title="Manage Course Details"
                    >
                      <DocumentTextIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(course._id)}
                      className="text-red-500 hover:text-red-400 p-2 rounded-lg hover:bg-red-500/10 transition-colors"
                      title="Delete Course"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isModalOpen && currentCourse && (
        <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditing ? 'Edit Course' : 'Add New Course'}>
          <form onSubmit={handleSubmit} className="space-y-4 text-neutral-light">
            <div>
              <label htmlFor="title" className="block text-sm font-medium mb-1">Title</label>
              <input type="text" name="title" id="title" value={currentCourse.title || ''} onChange={handleFormChange} required className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary"/>
            </div>
            <div>
              <label htmlFor="tagline" className="block text-sm font-medium mb-1">Tagline</label>
              <input type="text" name="tagline" id="tagline" value={currentCourse.tagline || ''} onChange={handleFormChange} className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary"/>
            </div>
            <div>
                <label htmlFor="category" className="block text-sm font-medium mb-1">Category</label>
                <select name="category" id="category" value={currentCourse.category || ''} onChange={handleFormChange} required className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary">
                    <option value="" disabled>Select category</option>
                    {courseCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
            </div>
            <div>
              <label htmlFor="description" className="block text-sm font-medium mb-1">Short Description</label>
              <textarea name="description" id="description" value={currentCourse.description || ''} onChange={handleFormChange} rows={3} className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary"></textarea>
            </div>
            <div>
              <label htmlFor="longDescription" className="block text-sm font-medium mb-1">Long Description</label>
              <textarea name="longDescription" id="longDescription" value={currentCourse.longDescription || ''} onChange={handleFormChange} rows={5} className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary"></textarea>
            </div>
             <div>
              <label htmlFor="curriculum" className="block text-sm font-medium mb-1">Curriculum (comma-separated)</label>
              <input type="text" name="curriculum" id="curriculum" value={currentCourse.curriculum?.join(', ') || ''} onChange={handleFormChange} className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary"/>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="price" className="block text-sm font-medium mb-1">Price</label>
                  <input type="number" name="price" id="price" value={currentCourse.price || 0} step="0.01" onChange={handleFormChange} required className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary"/>
                </div>
                <div>
                  <label htmlFor="duration" className="block text-sm font-medium mb-1">Duration (in hours)</label>
                  <input type="number" name="duration" id="duration" value={currentCourse.duration || 0} onChange={handleFormChange} required className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary" min="1" />
                </div>
            </div>
            <div>
              <label htmlFor="instructor" className="block text-sm font-medium mb-1">Instructor</label>
              <select name="instructor" id="instructor" value={currentCourse.instructor || ''} onChange={handleFormChange} required className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary">
                <option value="" disabled>Select instructor</option>
                {teamMembers.map(member => (
                  <option key={member._id} value={member._id}>{member.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="imageUrl" className="block text-sm font-medium mb-1">Image URL</label>
              <input type="text" name="imageUrl" id="imageUrl" value={currentCourse.imageUrl || ''} onChange={handleFormChange} className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary"/>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={closeModal} className="px-4 py-2 rounded bg-neutral-600 hover:bg-neutral-500 transition-colors">Cancel</button>
              <button type="submit" className="px-4 py-2 rounded bg-primary hover:bg-primary-dark text-white transition-colors" disabled={saving}>{isEditing ? 'Save Changes' : 'Add Course'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default ManageCoursesPage;